import React, { useContext, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { useTextDomainContext } from "@/app/providers";
import { ConfirmNotification, KeyTextField, TableView1 } from "@/components";
import {
  useAddSettingKey,
  useDeleteSettingKey,
  useSettingKeys,
  SettingKeyItem,
} from "@/hooks";

interface CustomTabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: CustomTabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-keys-tabpanel-${index}`}
      aria-labelledby={`settings-keys-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `settings-keys-tab-${index}`,
    "aria-controls": `settings-keys-tabpanel-${index}`,
  };
}

export const SettingsKeys = () => {
  const { TextDomainContext } = useTextDomainContext();
  const { gettext } = useContext(TextDomainContext);

  const allowedChars = /^[0-9a-z-]+$/;

  // Active tab state
  const [tabIndex, setTabIndex] = useState<number>(0);

  // Form states
  const [globalKeyInput, setGlobalKeyInput] = useState<string>("");
  const [selectedPartnerKey, setSelectedPartnerKey] = useState<string>("");
  const [selectedCorporateKey, setSelectedCorporateKey] = useState<string>("");
  const [selectedUserKey, setSelectedUserKey] = useState<string>("");

  // Delete dialog state
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState<boolean>(false);
  const [keyToDelete, setKeyToDelete] = useState<{
    id: string;
    key: string;
  } | null>(null);

  // Queries & Mutations
  const { settingKeys, loading, refetch } = useSettingKeys();
  const { addSettingKey, loading: addLoading } = useAddSettingKey();
  const { deleteSettingKey } = useDeleteSettingKey();

  // Filter keys by type
  const globalKeys = useMemo(
    () => settingKeys.filter((k: SettingKeyItem) => k.type === "global"),
    [settingKeys],
  );
  const partnerKeys = useMemo(
    () => settingKeys.filter((k: SettingKeyItem) => k.type === "partner"),
    [settingKeys],
  );
  const corporateKeys = useMemo(
    () => settingKeys.filter((k: SettingKeyItem) => k.type === "corporate"),
    [settingKeys],
  );
  const userKeys = useMemo(
    () => settingKeys.filter((k: SettingKeyItem) => k.type === "user"),
    [settingKeys],
  );

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
  };

  // Handlers for adding keys
  const handleAddGlobalKey = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!globalKeyInput || !allowedChars.test(globalKeyInput)) return;

    const success = await addSettingKey({
      key: globalKeyInput,
      type: "global",
    });
    if (success) {
      setGlobalKeyInput("");
      refetch();
    }
  };

  const handleAddPartnerKey = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedPartnerKey) return;

    const success = await addSettingKey({
      key: selectedPartnerKey,
      type: "partner",
    });
    if (success) {
      setSelectedPartnerKey("");
      refetch();
    }
  };

  const handleAddCorporateKey = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedCorporateKey) return;

    const success = await addSettingKey({
      key: selectedCorporateKey,
      type: "corporate",
    });
    if (success) {
      setSelectedCorporateKey("");
      refetch();
    }
  };

  const handleAddUserKey = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUserKey) return;

    const success = await addSettingKey({ key: selectedUserKey, type: "user" });
    if (success) {
      setSelectedUserKey("");
      refetch();
    }
  };

  // Handler for delete
  const handleDeleteClick = (id: string, key: string) => {
    setKeyToDelete({ id, key });
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async (data: any) => {
    if (data?.result === "yes") {
      const targetId = data?.parameters?.id || keyToDelete?.id;
      if (targetId) {
        const success = await deleteSettingKey(targetId);
        if (success) {
          refetch();
        }
      }
    }
    setDeleteConfirmOpen(false);
    setKeyToDelete(null);
  };

  const formatCellDate = (val: any) => {
    if (!val) return "-";
    try {
      const date = new Date(val);
      if (isNaN(date.getTime())) return "-";
      return date.toLocaleString();
    } catch {
      return "-";
    }
  };

  // Table Columns Definition
  const getColumns = () => [
    {
      field: "key",
      headerName: gettext("Key Name"),
      flex: 1.5,
      minWidth: 200,
    },
    {
      field: "createdByName",
      headerName: gettext("Created By"),
      flex: 1,
      minWidth: 160,
      renderCell: (params: any) => params.value || "-",
    },
    {
      field: "createdAt",
      headerName: gettext("Created At"),
      flex: 1.2,
      minWidth: 180,
      renderCell: (params: any) => formatCellDate(params.value),
    },
    {
      field: "updatedByName",
      headerName: gettext("Updated By"),
      flex: 1,
      minWidth: 160,
      renderCell: (params: any) => params.value || "-",
    },
    {
      field: "updatedAt",
      headerName: gettext("Updated At"),
      flex: 1.2,
      minWidth: 180,
      renderCell: (params: any) => formatCellDate(params.value),
    },
    {
      field: "actions",
      headerName: gettext("Actions"),
      width: 90,
      sortable: false,
      renderCell: (params: any) => (
        <Tooltip title={gettext("Delete Key")}>
          <IconButton
            color="error"
            onClick={() => handleDeleteClick(params.row.id, params.row.key)}
            size="small"
          >
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  const columns = getColumns();

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        {gettext("Settings Keys")}
      </Typography>
      <Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
        {gettext(
          "Define setting keys to standardize setting information stored in the database.",
        )}
      </Typography>

      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs
          value={tabIndex}
          onChange={handleTabChange}
          aria-label="Settings Keys Tabs"
        >
          <Tab label={gettext("Global Keys")} {...a11yProps(0)} />
          <Tab label={gettext("Partner Keys")} {...a11yProps(1)} />
          <Tab label={gettext("Corporate Keys")} {...a11yProps(2)} />
          <Tab label={gettext("User Keys")} {...a11yProps(3)} />
        </Tabs>
      </Box>

      {/* Global Keys Tab */}
      <CustomTabPanel value={tabIndex} index={0}>
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {gettext("Add Global Key")}
            </Typography>
            <Box component="form" onSubmit={handleAddGlobalKey}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  gap: 2,
                  alignItems: "flex-start",
                }}
              >
                <KeyTextField
                  label={gettext("Key Name")}
                  value={globalKeyInput}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setGlobalKeyInput(e.target.value)
                  }
                  placeholder="e.g. theme-mode"
                  sx={{ width: 350 }}
                  required
                />
                <Button
                  type="submit"
                  variant="contained"
                  disabled={
                    addLoading ||
                    !globalKeyInput ||
                    !allowedChars.test(globalKeyInput)
                  }
                  sx={{ height: 56 }}
                >
                  {gettext("Add Key")}
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>

        <TableView1
          rows={globalKeys}
          columns={columns}
          loading={loading}
          showGridToolbarQuickFilter
        />
      </CustomTabPanel>

      {/* Partner Keys Tab */}
      <CustomTabPanel value={tabIndex} index={1}>
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {gettext("Add Partner Key")}
            </Typography>
            {globalKeys.length === 0 ? (
              <Alert severity="warning">
                {gettext(
                  "No Global Keys available. Please add a Global Key under the Global Keys tab first.",
                )}
              </Alert>
            ) : (
              <Box component="form" onSubmit={handleAddPartnerKey}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    gap: 2,
                    alignItems: "flex-start",
                  }}
                >
                  <FormControl sx={{ width: 350 }}>
                    <InputLabel id="select-partner-key-label">
                      {gettext("Select Global Key")}
                    </InputLabel>
                    <Select
                      labelId="select-partner-key-label"
                      value={selectedPartnerKey}
                      label={gettext("Select Global Key")}
                      onChange={(e) => setSelectedPartnerKey(e.target.value)}
                    >
                      {globalKeys.map((item) => (
                        <MenuItem key={item.id} value={item.key}>
                          {item.key}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={addLoading || !selectedPartnerKey}
                    sx={{ height: 56 }}
                  >
                    {gettext("Add Key")}
                  </Button>
                </Box>
              </Box>
            )}
          </CardContent>
        </Card>

        <TableView1
          rows={partnerKeys}
          columns={columns}
          loading={loading}
          showGridToolbarQuickFilter
        />
      </CustomTabPanel>

      {/* Corporate Keys Tab */}
      <CustomTabPanel value={tabIndex} index={2}>
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {gettext("Add Corporate Key")}
            </Typography>
            {globalKeys.length === 0 ? (
              <Alert severity="warning">
                {gettext(
                  "No Global Keys available. Please add a Global Key under the Global Keys tab first.",
                )}
              </Alert>
            ) : (
              <Box component="form" onSubmit={handleAddCorporateKey}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    gap: 2,
                    alignItems: "flex-start",
                  }}
                >
                  <FormControl sx={{ width: 350 }}>
                    <InputLabel id="select-corporate-key-label">
                      {gettext("Select Global Key")}
                    </InputLabel>
                    <Select
                      labelId="select-corporate-key-label"
                      value={selectedCorporateKey}
                      label={gettext("Select Global Key")}
                      onChange={(e) => setSelectedCorporateKey(e.target.value)}
                    >
                      {globalKeys.map((item) => (
                        <MenuItem key={item.id} value={item.key}>
                          {item.key}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={addLoading || !selectedCorporateKey}
                    sx={{ height: 56 }}
                  >
                    {gettext("Add Key")}
                  </Button>
                </Box>
              </Box>
            )}
          </CardContent>
        </Card>

        <TableView1
          rows={corporateKeys}
          columns={columns}
          loading={loading}
          showGridToolbarQuickFilter
        />
      </CustomTabPanel>

      {/* User Keys Tab */}
      <CustomTabPanel value={tabIndex} index={3}>
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {gettext("Add User Key")}
            </Typography>
            {globalKeys.length === 0 ? (
              <Alert severity="warning">
                {gettext(
                  "No Global Keys available. Please add a Global Key under the Global Keys tab first.",
                )}
              </Alert>
            ) : (
              <Box component="form" onSubmit={handleAddUserKey}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    gap: 2,
                    alignItems: "flex-start",
                  }}
                >
                  <FormControl sx={{ width: 350 }}>
                    <InputLabel id="select-user-key-label">
                      {gettext("Select Global Key")}
                    </InputLabel>
                    <Select
                      labelId="select-user-key-label"
                      value={selectedUserKey}
                      label={gettext("Select Global Key")}
                      onChange={(e) => setSelectedUserKey(e.target.value)}
                    >
                      {globalKeys.map((item) => (
                        <MenuItem key={item.id} value={item.key}>
                          {item.key}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={addLoading || !selectedUserKey}
                    sx={{ height: 56 }}
                  >
                    {gettext("Add Key")}
                  </Button>
                </Box>
              </Box>
            )}
          </CardContent>
        </Card>

        <TableView1
          rows={userKeys}
          columns={columns}
          loading={loading}
          showGridToolbarQuickFilter
        />
      </CustomTabPanel>

      {/* Delete Confirmation Modal */}
      <ConfirmNotification
        isOpen={deleteConfirmOpen}
        title={gettext("Delete Key")}
        message={gettext(
          `Are you sure you want to delete key "${keyToDelete?.key}"?`,
        )}
        parameters={keyToDelete}
        getResult={handleConfirmDelete}
      />
    </Box>
  );
};
